import React, { FC, useState, useEffect } from 'react'
import {
    Avatar,
    Badge,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItemButton,
    Tab,
    Tabs,
    Typography,
} from '@mui/material'
import NewReleasesIcon from '@mui/icons-material/NewReleases'
import NotificationsIcon from '@mui/icons-material/Notifications'
import LaunchIcon from '@mui/icons-material/Launch'
import useAuth from '@/context/useAuth'
import { useTranslation } from 'react-i18next'
import { updateReleasesViews, getReleases } from '@/services/user.service'
import { Release } from '@/types/userPermission'
import DOMPurify from 'dompurify'

interface ReleaseApiResponse {
    ReleaseId: number
    Title: string
    Description: string
    CreatedAt: string
    RedirectUrl?: string | null
    HasSeen: number
    Path?: string | null
    ImageUrl?: string | null
    PictureUrl?: string | null
}

const ReleasesMenu: FC = () => {
    const { me } = useAuth()
    const { t } = useTranslation()

    const [listOpen, setListOpen] = useState(false)
    const [detailOpen, setDetailOpen] = useState(false)
    const [selectedRelease, setSelectedRelease] = useState<Release | null>(null)
    const [tab, setTab] = useState<'new' | 'old'>('new')
    const [releases, setReleases] = useState<Release[]>([])

    const isAgrid = me?.email?.includes('a-grid') ?? false
    const unseenCount = releases.filter((release) => !release?.hasSeen).length
    const newReleases = releases.filter((release) => !release?.hasSeen)
    const oldReleases = releases.filter((release) => release?.hasSeen)
    const filteredReleases = tab === 'new' ? newReleases : oldReleases

    useEffect(() => {
        if (isAgrid) {
            fetchReleases()
        }
    }, [isAgrid])

    const fetchReleases = async () => {
        try {
            const data = await getReleases()
            const mappedReleases = (data?.releases ?? []).map(
                (release: ReleaseApiResponse): Release => ({
                    releaseId: String(release.ReleaseId),
                    title: release.Title,
                    description: release.Description,
                    createdAt: release.CreatedAt,
                    redirectUrl: release.RedirectUrl ?? undefined,
                    hasSeen: release.HasSeen === 1,
                    path: release.Path ?? undefined,
                    imageUrl: release.ImageUrl ?? undefined,
                    pictureUrl: release.PictureUrl ?? undefined,
                })
            )
            setReleases(mappedReleases.slice(0, 5))
        } catch (error) {
            // Failed to fetch releases, keep empty array
        }
    }

    if (!isAgrid || releases.length === 0) {
        return null
    }

    const getReleaseMedia = (release?: Release | null) => release?.path || release?.imageUrl || release?.pictureUrl
    const getMediaType = (url?: string | null) => {
        if (!url) return 'none'
        const cleanUrl = url.split('?')[0].toLowerCase()
        if (cleanUrl.match(/\.(mp4|webm|ogg|mov)$/)) return 'video'
        if (cleanUrl.match(/\.(png|jpe?g|gif|webp|svg|avif)$/)) return 'image'
        return 'unknown'
    }
    const getSafeHtml = (html?: string | null) => ({ __html: DOMPurify.sanitize(html || '') })
    const getPlainText = (html?: string | null) =>
        DOMPurify.sanitize(html || '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
    const getPreviewText = (html?: string | null, maxLength = 100) => {
        const text = getPlainText(html).replace(/\s+/g, ' ').trim()
        if (text.length <= maxLength) return text
        return `${text.slice(0, maxLength).trimEnd()}…`
    }

    const handleOpenList = () => {
        setListOpen(true)
    }

    const handleCloseList = () => {
        setListOpen(false)
    }

    const handleSelectRelease = (release: Release) => {
        setSelectedRelease(release)
        setDetailOpen(true)
        setListOpen(false)
    }

    const handleTabChange = (_: React.SyntheticEvent, value: 'new' | 'old') => {
        setTab(value)
    }

    const markReleaseSeen = async (release?: Release | null) => {
        if (!release?.releaseId) return
        await updateReleasesViews({ releaseId: release.releaseId })
        await fetchReleases()
    }

    const handleDetailClose = async () => {
        const release = selectedRelease
        setDetailOpen(false)
        setSelectedRelease(null)
        await markReleaseSeen(release)
    }

    const handleLearnMore = () => {
        const release = selectedRelease;
        markReleaseSeen(release)
        if (release?.redirectUrl) {
            window.open(release.redirectUrl, '_blank')
        }
        handleDetailClose()
    }

    const actionButton = (
        <Badge color="error" badgeContent={unseenCount} invisible={unseenCount === 0}>
            <IconButton color="inherit" onClick={handleOpenList} aria-label={t('auth.releases')}>
                <NotificationsIcon />
            </IconButton>
        </Badge>
    )

    return (
        <>
            {actionButton}

            <Dialog open={listOpen} onClose={handleCloseList} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NewReleasesIcon color="primary" />
                    {t('auth.releases')}
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab value="new" label={t('auth.new')} />
                        <Tab value="old" label={t('auth.old')} />
                    </Tabs>
                    <List disablePadding>
                        {filteredReleases.length === 0 && (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {tab === 'new' ? t('auth.noNewNotifications') : t('auth.noOldNotifications')}
                                </Typography>
                            </Box>
                        )}
                        {filteredReleases.map((release, index) => {
                            const previewSrc = getReleaseMedia(release)
                            const previewType = getMediaType(previewSrc)
                            return (
                                <ListItemButton
                                    key={release.releaseId || index}
                                    onClick={() => handleSelectRelease(release)}
                                    alignItems="flex-start"
                                    sx={{
                                        borderBottom: index < filteredReleases.length - 1 ? '1px solid' : 'none',
                                        borderColor: 'divider',
                                        py: 2,
                                        px: 2.5,
                                        display: 'flex',
                                        gap: 2,
                                    }}
                                >
                                    <Box sx={{ width: '60%', flexShrink: 0 }}>
                                        {previewType === 'image' && previewSrc ? (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    paddingTop: '75%',
                                                    position: 'relative',
                                                    borderRadius: 2,
                                                    overflow: 'hidden',
                                                    bgcolor: 'grey.100',
                                                }}
                                            >
                                                <img
                                                    src={previewSrc}
                                                    alt={release.title}
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                    }}
                                                />
                                            </Box>
                                        ) : previewType === 'video' && previewSrc ? (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    paddingTop: '75%',
                                                    position: 'relative',
                                                    borderRadius: 2,
                                                    overflow: 'hidden',
                                                    bgcolor: 'grey.900',
                                                }}
                                            >
                                                <video
                                                    src={previewSrc}
                                                    autoPlay
                                                    loop
                                                    muted
                                                    playsInline
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                    }}
                                                />
                                            </Box>
                                        ) : (
                                            <Avatar
                                                variant="rounded"
                                                sx={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    aspectRatio: '4/3',
                                                    bgcolor: 'primary.main',
                                                }}
                                            >
                                                <NewReleasesIcon fontSize="large" />
                                            </Avatar>
                                        )}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, padding: '20px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                {release.title}
                                            </Typography>
                                        </Box>
                                        {/* <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            component="div"
                                            sx={{ display: 'block' }}
                                        >
                                            {getPreviewText(release.description)}
                                        </Typography> */}
                                        <Button
                                            variant="contained"
                                            startIcon={<LaunchIcon />}
                                            onClick={handleLearnMore}
                                            style={{ marginTop: 30 }}
                                        >
                                            {t('auth.learnMore')}
                                        </Button>
                                    </Box>
                                </ListItemButton>
                            )
                        })}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseList}>{t('back')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={detailOpen} onClose={handleDetailClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NewReleasesIcon color="primary" />
                </DialogTitle>

                <DialogContent>
                    {getMediaType(getReleaseMedia(selectedRelease)) === 'image' && getReleaseMedia(selectedRelease) && (
                        <Box
                            sx={{
                                mb: 3,
                                display: 'flex',
                                justifyContent: 'center',
                                borderRadius: 2,
                                overflow: 'hidden',
                            }}
                        >
                            <img
                                src={getReleaseMedia(selectedRelease)}
                                alt={selectedRelease?.title}
                                style={{
                                    maxWidth: '100%',
                                    // maxHeight: 200,
                                    height: 'auto',
                                    borderRadius: '8px',
                                }}
                            />
                        </Box>
                    )}
                    {getMediaType(getReleaseMedia(selectedRelease)) === 'video' && getReleaseMedia(selectedRelease) && (
                        <Box
                            sx={{
                                mb: 3,
                                display: 'flex',
                                justifyContent: 'center',
                                borderRadius: 2,
                                overflow: 'hidden',
                                backgroundColor: 'grey.900',
                            }}
                        >
                            <video
                                src={getReleaseMedia(selectedRelease) || undefined}
                                controls
                                autoPlay
                                loop
                                muted
                                style={{
                                    maxWidth: '100%',
                                    // maxHeight: 300,
                                    height: 'auto',
                                }}
                            />
                        </Box>
                    )}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            {selectedRelease?.title}
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            component="div"
                            dangerouslySetInnerHTML={getSafeHtml(selectedRelease?.description)}
                        />
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default ReleasesMenu
